import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { QueryResult } from "@apollo/react-common";
import { FileFilled, FileTwoTone, InboxOutlined } from "@ant-design/icons";
import {
	Grid,
	Spin,
	Result,
	Form,
	Space,
	Button,
	Select,
	Upload,
	Input,
	Row,
	Col,
	Divider,
	message,
	Typography,
	Table,
	List,
} from "antd";
import { ColumnsType } from "antd/es/table";

import { AppPageLayout } from "../../layouts";

import {
	GET_DOCUMENTS,
	UPLOAD_DOCUMENT,
	GET_DOCUMENT_TYPES,
} from "../../queries";
import { GetDocuments } from "../../__generated__/GetDocuments";
import { GetDocumentTypes } from "../../__generated__/GetDocumentTypes";
import { Document as Doc } from "../../__generated__/Document";
import { DocumentType as DocType } from "../../__generated__/DocumentType";
import {
	UploadDocument,
	UploadDocumentVariables,
} from "../../__generated__/UploadDocument";
import { Document } from "../../__generated__/Document";

import { UploadInformation } from "./UploadInformation";

const layout = {
	labelCol: { span: 4 },
	wrapperCol: { span: 20 },
	colon: false,
	labelAlign: "left" as const,
};

const DocumentUploadForm: React.FC<{ documentTypes: DocType[] }> = ({
	documentTypes,
}) => {
	const [file, setFile] = useState<File | null>(null);

	const [uploadDocument, { loading: mutationLoading }] = useMutation<
		UploadDocument,
		UploadDocumentVariables
	>(UPLOAD_DOCUMENT, {
		update(cache, { data }) {
			try {
				const { documents }: any = cache.readQuery({ query: GET_DOCUMENTS });
				cache.writeQuery({
					query: GET_DOCUMENTS,
					data: { documents: [data!.uploadDocument, ...documents] },
				});
			} catch {}
		},
	});

	const beforeUpload = (file: File) => {
		setFile(null);
		if (file.type !== "image/jpeg" && file.type !== "application/pdf") {
			message.error("Document must be JPEG or PDF");

			return false;
		}
		if (file.size / 1024 / 1024 > 6) {
			message.error("Document file size must be 6MB or less");
			return false;
		}
		setFile(file);
		return false;
	};
	const onFinish = (data: any) => {
		uploadDocument({
			variables: {
				typeID: data.type,
				file: file,
			},
		})
			.then((data) => {
				message.success("Document Uploaded.");
				setFile(null);
			})
			.catch((err) => {
				message.error("Error uploading document.");
			});
	};
	return (
		<Spin spinning={mutationLoading}>
			<ul>
				<li>
					You can upload documents in <strong>JPEG</strong> or{" "}
					<strong>PDF</strong> format
				</li>
				<li>
					Maximum file size of a single document is <strong>6MB</strong>
				</li>
			</ul>
			<Upload.Dragger
				beforeUpload={beforeUpload}
				showUploadList={false}
				accept="application/pdf,image/jpeg"
			>
				{file ? (
					<>
						<p>
							<Typography.Title level={2}>
								Uploading {file.name}
							</Typography.Title>
						</p>
						<p className="ant-upload-drag-icon">
							<FileFilled />
						</p>
						<p className="ant-upload-text">
							Click or drag new file to this area to replace it.
						</p>
					</>
				) : (
					<>
						<p className="ant-upload-drag-icon">
							<InboxOutlined />
						</p>
						<p className="ant-upload-text">
							Click or drag a file to this area.
						</p>
					</>
				)}
			</Upload.Dragger>
			{file && (
				<Form onFinish={onFinish} {...layout}>
					<Divider />
					<Form.Item
						name="type"
						label="Type"
						rules={[{ required: true, message: "Select document type." }]}
					>
						<Select>
							{documentTypes.map((dt) => (
								<Select.Option value={dt.id}>{dt.name}</Select.Option>
							))}
						</Select>
					</Form.Item>
					<Button type="primary" block htmlType="submit">
						Upload
					</Button>
				</Form>
			)}
		</Spin>
	);
};

const columns: ColumnsType<Document> = [
	{
		title: "Type",
		dataIndex: ["type", "name"],
	},
	{
		title: "File",
		dataIndex: "filename",
	},
	{
		title: "Uploaded At",
		dataIndex: "createdAt",
		render: (createdAt: string) => {
			const date = new Date(createdAt);
			return date.toLocaleString();
		},
	},
];

export const DocumentIndexPage: React.FC = () => {
	const documents = useQuery<GetDocuments>(GET_DOCUMENTS);
	const documentTypes = useQuery<GetDocumentTypes>(GET_DOCUMENT_TYPES);
	const screens = Grid.useBreakpoint();

	return (
		<AppPageLayout title="Your Documents">
			<Helmet>
				<title>Your Documents</title>
			</Helmet>
			<Row gutter={20}>
				<Col span={24}>
					<UploadInformation />
				</Col>
				<Col span={24}>
					<Row>
						<Col span={24}>
							<DocumentUploadFormContainer
								documents={documents}
								documentTypes={documentTypes}
							/>
						</Col>
					</Row>
				</Col>
			</Row>
		</AppPageLayout>
	);
};

const DocumentUploadFormContainer: React.FC<{
	documents: QueryResult<GetDocuments, Record<string, any>>;
	documentTypes: QueryResult<GetDocumentTypes, Record<string, any>>;
}> = ({ documents, documentTypes }) => (
	<Spin spinning={documents.loading || documentTypes.loading}>
		{documents.error || documentTypes.error ? (
			<Result status="500" title="Error loading documents" />
		) : (
			<>
				<DocumentUploadForm
					documentTypes={
						documentTypes.data ? documentTypes.data!.documentTypes : []
					}
				/>
				<Divider />
				<List
					dataSource={documents.data ? documents.data!.documents : []}
					rowKey="id"
					header="Uploaded Documents"
					bordered={true}
					style={{ backgroundColor: "white" }}
					renderItem={(item) => {
						const date = new Date(item.createdAt);
						return (
							<List.Item>
								<List.Item.Meta
									title={item.type.name}
									description={item.filename}
									avatar={<FileTwoTone style={{ fontSize: "48px" }} />}
								/>
							</List.Item>
						);
					}}
				/>
			</>
		)}
	</Spin>
);
